import { ZodRawShape, z } from "zod";

import { ORDER_STATUS_ROUTE_COLOR } from "@web-app/pages/common/DispatchMap/utils";
import { AppFilterType, CustomField, Route, User } from "@web-app/types";

import { GoogleAddress } from "../types/google";
import { Order, OrderDeliveryType } from "../types/orders";

import { customFieldSchema } from "./customField";

export const ORDER_STATUS_COLORS = {
  ASSIGNED: {
    card: {
      background: "bg-status-purple-bg",
      border: "border-status-purple hover:border-purple-700",
      darkBackground: "bg-purple-400",
    },
    lightText: "text-status-purple",
    route: "#9747FF",
    text: "text-status-purple",
  },
  CANCELLED: {
    card: {
      background: "bg-status-red-bg",
      border: "bg-status-red hover:border-red-500",
      darkBackground: "bg-red-500",
    },
    lightText: "text-red-300",
    route: "#FD4242",
    text: "text-status-red",
  },
  COMPLETE: {
    card: {
      background: "bg-status-green-bg",
      border: "bg-status-green hover:border-green-700",
      darkBackground: "bg-green-700",
    },
    lightText: "text-green-500",
    progressBar: "bg-gray-500",
    route: "#008760",
    text: "text-status-green",
  },
  FAILED: {
    card: {
      background: "bg-status-red-bg",
      border: "bg-status-red hover:border-red-500",
      darkBackground: "bg-red-500",
    },
    lightText: "text-red-300",
    route: "#FD4242",
    text: "text-status-red",
  },

  INPROGRESS: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },
  ORDER_ARRIVED_AT_DROPOFF: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },
  ORDER_ARRIVED_AT_PICKUP: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },
  ORDER_CANCELLED: {
    card: {
      background: "bg-status-red-bg",
      border: "bg-status-red hover:border-red-500",
      darkBackground: "bg-red-500",
    },
    lightText: "text-red-300",
    route: "#FD4242",
    text: "text-status-red",
  },
  ORDER_DROPPED_OFF: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },
  ORDER_FAILED: {
    card: {
      background: "bg-status-red-bg",
      border: "bg-status-red hover:border-red-500",
      darkBackground: "bg-red-500",
    },
    lightText: "text-red-300",
    route: "#FD4242",
    text: "text-status-red",
  },
  ORDER_IN_TRANSIT_TO_DROPOFF: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },

  ORDER_IN_TRANSIT_TO_PICKUP: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },
  ORDER_PICKED_UP: {
    card: {
      background: "bg-status-blue-bg",
      border: "border-status-blue hover:border-blue-500",
      darkBackground: "bg-blue-500",
    },
    lightText: "text-status-blue",
    progressBar: "bg-gray-300",
    route: "#28BFFF",
    text: "text-status-blue",
  },
  TODO: {
    card: {
      background: "bg-white",
      border: "border-gray-100 hover:border-gray-400",
      darkBackground: "bg-black",
    },
    lightText: "text-gray-700",
    route: "#000000",
    text: "text-gray-700",
  },

  UNASSIGNED: {
    card: {
      background: "bg-gray-100",
      border: "border-gray-600 hover:border-gray-700",
      darkBackground: "bg-gray-600",
    },
    lightText: "text-gray-100",
    route: "#575C64",
    text: "text-gray-600",
  },
};

const anchorStyle = "w-[18px] h-[22px]";
export const mapLegendOptions = [
  {
    color: ORDER_STATUS_COLORS["TODO"].text,
    image: "/markers/PICKUP_TODO.webp",
    label: "Unassigned",
    style: anchorStyle,
  },
  {
    color: ORDER_STATUS_COLORS["ASSIGNED"].text,
    image: "/markers/PICKUP_ASSIGNED.webp",
    label: "Assigned",
    style: anchorStyle,
  },
  {
    color: ORDER_STATUS_COLORS["INPROGRESS"].text,
    image: "/markers/PICKUP_INPROGRESS.webp",
    label: "In Transit",
    style: anchorStyle,
  },
  {
    color: ORDER_STATUS_COLORS["COMPLETE"].text,
    image: "/markers/PICKUP_COMPLETE.webp",
    label: "Completed",
    style: anchorStyle,
  },
  {
    color: ORDER_STATUS_COLORS["CANCELLED"].text,
    image: "/markers/PICKUP_CANCELLED.webp",
    label: "Failed/Cancelled",
    style: anchorStyle,
  },
  {
    color: ORDER_STATUS_COLORS["COMPLETE"].text,
    image: "/markers/driver_ONLINE.webp",
    label: "Driver Idle",
    style: "w-5 h-5",
  },
  {
    color: ORDER_STATUS_COLORS["INPROGRESS"].text,
    image: "/markers/driver_INTRANSIT.webp",
    label: "Driver In-transit",
    style: "w-5 h-5",
  },
  {
    color: ORDER_STATUS_COLORS["TODO"].text,
    image: "/markers/PICKUP_TODO.webp",
    label: "Pickup",
    style: anchorStyle,
  },
  {
    color: ORDER_STATUS_COLORS["TODO"].text,
    image: "/markers/DROPOFF_TODO.webp",
    label: "Drop-off",
    style: anchorStyle,
  },
];

const LocationDetails = z.object({
  address: z.string(),
  businessName: z.string(),
  name: z.string(),
  note: z.string(),
  phone: z.string(),
});

const OrderDetails = z
  .object({
    currency: z.enum(["AUD", "CAD", "EUR", "GBP", "JPY", "USD"]),
    description: z.string(),
    distanceString: z.string(),
    itemsCount: z.number(),
    serviceDuration: z.number(),
    tip: z.number(),
    value: z.number(),
  })
  .partial();

const Requirements = z.object({
  itemChecklist: z.boolean().optional(),
  note: z.boolean(),
  photo: z.boolean(),
  signature: z.boolean(),
});
export const createOrderRouteType = z.object({
  assigneeId: z.string().nullable().default(null),
  connectedWorkspaceId: z.nullable(z.string()).optional(),
  customerEmail: z.string().optional().nullable(),
  customFields: z.array(
    z.object({
      definition: customFieldSchema,
      values: z.array(
        z.object({
          isSelected: z.boolean(),
          value: z.string(),
        }),
      ),
    }),
  ),
  deliveryPlaceId: z.string().nullable().optional(),
  destinationAddress: z.string().optional(),
  details: OrderDetails,
  dropoff: LocationDetails,
  dropoffChecklist: z
    .array(
      z.object({
        isChecked: z.boolean(),
        isMandatory: z.boolean(),
        label: z.string(),
        quantity: z.number(),
      }),
    )
    .optional(),
  dropoffLatLng: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .optional(),
  dropoffLocation: z
    .object({
      coordinates: z.number().array(),
      type: z.string(),
    })
    .optional(),
  dropoffRequirements: Requirements.optional(),
  externalId: z.string(),
  geoDropoffDateTime: z.date().optional().nullable(),
  geoPickupDateTime: z.date().optional().nullable(),
  id: z.string().optional(),
  isOrderSourceAConnectedWorkspace: z.boolean().optional(),
  minVehicle: z.enum([
    "BICYCLE",
    "BOX_TRUCK",
    "E-BIKE",
    "E-SCOOTER",
    "MOTORCYCLE",
    "OTHER",
    "PICKUP_TRUCK",
    "SEDAN",
    "SUV",
    "SPRINTER_VAN",
    "WALKING",
  ]),
  pickup: LocationDetails,
  pickupAddress: z.string().optional(),
  pickupChecklist: z
    .array(
      z.object({
        isChecked: z.boolean(),
        isMandatory: z.boolean(),
        label: z.string(),
        quantity: z.number(),
      }),
    )
    .optional(),
  pickupLatLng: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .optional(),
  pickupLocation: z
    .object({
      coordinates: z.number().array(),
      type: z.string(),
    })
    .optional(),
  pickupPlaceId: z.string().nullable().optional(),
  pickupRequirements: Requirements.optional(),
  priority: z.enum(["HIGH", "LOW", "NORMAL"]),
  scheduledEndDateTime: z.date(),
  scheduledStartDateTime: z.date(),
  sourceId: z.string().optional(),
  status: z
    .enum(["TODO", "INPROGRESS", "COMPLETE", "FAILED", "CANCELLED", "ASSIGNED"])
    .nullable()
    .optional()
    .default("TODO"),
  type: z.enum([
    "ROUTE_OPT_PICKUP",
    "ROUTE_OPT_DROPOFF",
    "INDIVIDUAL_PICKUP",
    "INDIVIDUAL_DROPOFF",
    "INDIVIDUAL_PICKUP_AND_DROPOFF",
  ]),
});

export type GetOrderParamsType = {
  driverIds: string;
  endDate: string;
  groupBy: "assignee" | "status" | "workspace";
  offset: number;
  sortBy?: "asc" | "desc";
  sortColumn: string;
  startDate: string;
  stateIds: string;
  status: string;
  workspaceIds: string;
};

export type CreateOrderType = z.infer<typeof createOrderRouteType>;

export const pickupChecklistSchema = z
  .array(
    z.object({
      isChecked: z.boolean(),
      isMandatory: z.boolean(),
      label: z.string().min(1, "Item name can't be empty"),
      quantity: z.coerce
        .number()
        .gte(1, "Must be 1 or above")
        .lt(1000, "Must not exceed 999"),
    }),
  )
  .optional();
const orderFormSchema = z.object({
  connectedWorkspaceId: z.nullable(z.string()).optional(),
  description: z
    .string({ required_error: "This field is required." })
    .optional(),
  externalId: z.nullable(z.string()),
  isOrderSourceAConnectedWorkspace: z.boolean().optional(),
  itemsCount: z
    .string({
      invalid_type_error: "Item Count must be a number",
      required_error: "This field is required.",
    })
    .refine((value) => parseInt(value) >= 0, {
      message: "Item count must be greater than or equal to 0.",
    })
    .transform((value) => parseInt(value)),
  orderRequirements_ITEMCHECKLIST: z.boolean(),
  orderRequirements_NOTES: z.boolean(),
  orderRequirements_PHOTO: z.boolean(),
  orderRequirements_SIGNATURE: z.boolean(),
  pickupChecklist: pickupChecklistSchema,
  serviceTime: z.number().optional(),
  tip: z
    .string({
      invalid_type_error: "Tip must be a number",
      required_error: "This field is required.",
    })
    .refine((value) => parseInt(value) >= 0, {
      message: "Tip must be greater than or equal to 0.",
    })
    .transform((value) => parseFloat(value)),
  value: z
    .string({
      invalid_type_error: "Value must be a number",
      required_error: "This field is required.",
    })
    .refine((value) => parseInt(value) >= 0, {
      message: "Value must be greater than or equal to 0.",
    })
    .transform((value) => parseFloat(value)),
  vehicleType: z.enum(
    [
      "WALK",
      "CYCLE",
      "BIKE",
      "SEDAN",
      "ESTATE",
      "VAN",
      "PICK_UP",
      "SUV",
      "TRUCK",
      "OTHER",
    ],
    {
      required_error: "Mode of Transportation is required.",
    },
  ),
});

const dropOffOrderSchema = {
  dropOffBusinessName: z.string().optional(),
  dropOffEndTime: z.date().optional(),
  dropoffLocation: z.custom<GoogleAddress>(
    (value) => !!value || !!value?.description,
    {
      message: "This field is required.",
    },
  ),
  dropOffName: z.string().min(1, { message: "This field is required." }),
  dropOffNote: z.string().optional(),
  dropOffPhone: z.string().refine(
    (value) => {
      const pattern = /^\d{10,10}$/;

      return pattern.test(value);
    },
    {
      message: "Please enter a valid phone number (e.g. +1234567890)",
    },
  ),
  dropOffServiceTime: z
    .string()
    .refine((value) => !value || parseInt(value) >= 0, {
      message: "Service time must be greater than or equal to 0.",
    })
    .transform((value) => parseInt(value))
    .optional(),
  serviceTimeDropoff: z.number().optional(),
};

const pickupOrderSchema = {
  pickupBusinessName: z.string().optional(),
  pickupLocation: z.custom<GoogleAddress>(
    (value) => !!value || !!value?.description,
    {
      message: "This field is required.",
    },
  ),
  pickupName: z.string().min(1, { message: "This field is required." }),
  pickupNote: z.string().optional(),
  pickupPhone: z.string().refine(
    (value) => {
      const pattern = /^\d{10,10}$/;

      return pattern.test(value);
    },
    {
      message: "Please enter a valid phone number (e.g. +1234567890)",
    },
  ),
  pickupServiceTime: z
    .string()
    .refine((value) => parseInt(value) >= 0, {
      message: "Service time must be greater than or equal to 0.",
    })
    .transform((value) => parseInt(value))
    .optional(),
  pickupStartTime: z.date().optional(),
};

const individualPickupDropoffSchema = orderFormSchema.extend({
  ...dropOffOrderSchema,
  ...pickupOrderSchema,
  ...{
    scheduledEndDateTime: z.date(),
    scheduledStartDateTime: z.date(),
    timezone: z.string().optional(),
  },
});

const pickupSchema = orderFormSchema.extend({
  ...pickupOrderSchema,
  ...{
    pickupEndTime: z.date(),
    scheduledStartDateTime: z.date(),
    timezone: z.string().optional(),
  },
});

const dropoffSchema = orderFormSchema.extend({
  ...dropOffOrderSchema,
  ...{
    dropOffStartTime: z.date(),
    scheduledEndDateTime: z.date(),
    timezone: z.string().optional(),
  },
});

const getCustomFieldSchema = (customFieldsData?: CustomField[]) =>
  customFieldsData?.reduce((accum: ZodRawShape, item) => {
    switch (item.type) {
      case "NUMBER": {
        accum[item.name] = z.number().optional();
        break;
      }
      default: {
        accum[item.name] = z.string().optional();
      }
    }

    return accum;
  }, {}) || {};

export const getSchema = (
  orderType: OrderDeliveryType,
  customFieldsData?: CustomField[],
) => {
  const customFieldSchema = getCustomFieldSchema(customFieldsData);

  if (orderType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
    const individualMappedSchema = individualPickupDropoffSchema
      .extend(customFieldSchema)
      .refine(
        (data) =>
          !data.orderRequirements_ITEMCHECKLIST ||
          (data.pickupChecklist && data.pickupChecklist.length > 0),
        {
          message: "Required to add item checklist.",
          path: ["orderRequirements_ITEMCHECKLIST"],
        },
      );

    return individualMappedSchema;
  } else if (orderType === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
    return dropoffSchema.extend(customFieldSchema);
  }

  return pickupSchema.extend(customFieldSchema);
};
type IndividualSchemaType = z.infer<typeof individualPickupDropoffSchema>;
type PickupSchemaType = z.infer<typeof pickupSchema>;
type DropoffSchemaType = z.infer<typeof dropoffSchema>;

type RequiredFieldsType =
  | {
      orderType: OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;
      propertyName: keyof IndividualSchemaType;
    }
  | {
      orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF;
      propertyName: keyof DropoffSchemaType;
    }
  | {
      orderType: OrderDeliveryType.ROUTE_OPT_PICKUP;
      propertyName: keyof PickupSchemaType;
    };

export const getIsRequired = ({
  propertyName,
  orderType,
}: RequiredFieldsType) => {
  if (orderType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
    return !(
      individualPickupDropoffSchema.shape[propertyName] instanceof z.ZodOptional
    );
  } else if (orderType === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
    return !(dropoffSchema.shape[propertyName] instanceof z.ZodOptional);
  } else {
    return !(pickupSchema.shape[propertyName] instanceof z.ZodOptional);
  }
};

export const vehicleOptions = [
  {
    disabled: false,
    label: "Bike",
    value: "BIKE",
  },
  {
    disabled: false,
    label: "Cycle",
    value: "CYCLE",
  },
  {
    disabled: false,
    label: "Estate",
    value: "ESTATE",
  },
  {
    disabled: false,
    label: "Other",
    value: "OTHER",
  },
  {
    disabled: false,
    label: "Pick Up",
    value: "PICK_UP",
  },
  {
    disabled: false,
    label: "Sedan",
    value: "SEDAN",
  },
  {
    disabled: false,
    label: "SUV",
    value: "SUV",
  },
  {
    disabled: false,
    label: "Truck",
    value: "TRUCK",
  },
  {
    disabled: false,
    label: "Van",
    value: "VAN",
  },
  {
    disabled: false,
    label: "Walk",
    value: "WALK",
  },
];

export const schemaKeyValueMapper: {
  [key: string]: string;
} = {
  connectedWorkspaceId: "Order Source",
  description: "Description",
  dropOffBusinessName: "Business Name (Drop-off)",
  dropoffLocation: "Drop-off Location",
  dropOffName: "Full Name (Drop-off)",
  dropOffNote: "Note (Drop-off)",
  dropOffPhone: "Phone Number (Drop-off)",
  dropOffStartTime: "Pickup At (Start Time) / Drop-off After (Date/Time)",
  itemsCount: "Item Count",
  pickupBusinessName: "Business Name",
  pickupEndTime: "Pickup Before (Date/Time)",
  pickupLocation: "Pickup Location",
  pickupName: "Full Name (Pickup)",
  pickupNote: "Note",
  pickupPhone: "Phone Number (Pickup)",
  scheduledEndDateTime: "Drop-off Before (Date/Time) / Drop-off At (End Time)",
  scheduledStartDateTime: "Pickup At (Start Time) / Pickup After (Date/Time)",
  serviceTime: "Service Time (in minutes)",
  serviceTimeDropoff: "Service Time (in minutes)",
  tip: "Tip",
  value: "Value",
  vehicleType: "Mode of Transportation",
};

export const ROUTE_STATUS_BG_COLORS: Record<
  string,
  (typeof ORDER_STATUS_COLORS)[keyof typeof ORDER_STATUS_COLORS]
> = {
  Assigned: ORDER_STATUS_COLORS.ASSIGNED,
  Completed: ORDER_STATUS_COLORS.COMPLETE,
  "In Transit": ORDER_STATUS_COLORS.INPROGRESS,
  "Not Started": ORDER_STATUS_COLORS.UNASSIGNED,
};

export const ROUTE_STATUS_COLORS: Record<
  string,
  (typeof ORDER_STATUS_ROUTE_COLOR)[keyof typeof ORDER_STATUS_ROUTE_COLOR]
> = {
  Assigned: ORDER_STATUS_ROUTE_COLOR["Assigned"],
  Completed: ORDER_STATUS_ROUTE_COLOR["Completed"],
  "In Transit": ORDER_STATUS_ROUTE_COLOR["In Transit"],
  "Not Started": ORDER_STATUS_ROUTE_COLOR["Unassigned"],
};

export const REPORT_PILL_COLOR = "bg-red-700";

export const filterOrderByDriverStatus = (
  appFilter: AppFilterType,
  orders?: Map<string, Order>,
  assignees?: Map<string, User>,
) => {
  const filteredOrder = Array.from(orders!.values()).filter((order) => {
    const assignee = assignees?.get(order?.assigneeId || "");
    if (
      assignee?.driverDetails.dutyStatus &&
      appFilter.driverStatus.length &&
      !appFilter.driverStatus.includes(assignee?.driverDetails.dutyStatus)
    ) {
      return false;
    }

    return true;
  });

  return new Map(filteredOrder.map((item) => [item.id, item]));
};

export {
  dropOffOrderSchema,
  dropoffSchema,
  individualPickupDropoffSchema,
  pickupOrderSchema,
  pickupSchema,
};

export const errorMessages = {
  dropoff: {
    dropOffBusinessName: "Business name is required",
    dropoffLocation: "Drop-off location is required",
    dropOffName: "Full name is required",
    dropOffNote: "Note is required",
    dropOffPhone: "Phone number must be 10 digits",
    dropOffServiceTime: "Service time must be a positive number",
    dropOffStartTime: "Drop-off time is required",
    scheduledEndDateTime: "End time is required",
  },
  order: {
    description: "Description is required",
    itemsCount: "Item count is required",
    tip: "Tip is required",
    value: "Value is required",
    vehicleType: "Mode of transportation is required",
  },
  pickup: {
    pickupBusinessName: "Business name is required",
    pickupLocation: "Pickup location is required",
    pickupName: "Full name is required",
    pickupNote: "Note is required",
    pickupPhone: "Phone number must be 10 digits",
    pickupServiceTime: "Service time must be a positive number",
    pickupStartTime: "Pickup time is required",
  },
} as const;

export const routeHasEsa = (route?: Route | null): boolean =>
  !!(
    route?.externalAssignee?.type && route?.externalAssignee.deletedAt === null
  );

export const hasExternalAssignee = (order: Order) => {
  const orderHasExternalAssignee =
    order.externalAssignee && order.externalAssignee.deletedAt === null;
  if (
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF ||
    !order.route
  ) {
    return orderHasExternalAssignee;
  }

  return routeHasEsa(order.route);
};
